import React, { useEffect, useMemo, useState } from 'react';
import { ClipboardList, AlertTriangle, Download, ChevronRight } from 'lucide-react';
import SearchableDropdown from '../../components/SearchableDropdown';
import { InventoryPage } from './InventoryChrome';
import { REPORTS, money, type ReportDef, type ReportResult, type ReportColumn } from './reports/registry';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * REPORTS — mockup screen 9, built as a REGISTRY SHELL rather than one
 * hardcoded page per report (per the overnight addendum): every report on
 * this page is one entry in `reports/registry.ts`. Adding a new report in the
 * future is a registry entry + an `endpoint` function — nothing here changes.
 */

function formatCell(value: any, format?: ReportColumn['format']): string {
  if (value === null || value === undefined) return '—';
  switch (format) {
    case 'money': return money(value);
    // W5 review I7 — every 'percent' column the server declares (vendors.ts's
    // onTimePct/defectPct) returns a FRACTION (0..1), not a percent point. A
    // flawless vendor was rendering as "on-time 1%" and a 5-of-6 vendor as
    // "on-time 0.8333333333333334%".
    case 'percent': return `${Math.round(Number(value) * 100)}%`;
    case 'date': return new Date(value).toLocaleDateString();
    case 'number': return String(value);
    default: return String(value);
  }
}

function toCsv(result: ReportResult): string {
  const header = result.columns.map((c) => `"${c.label.replace(/"/g, '""')}"`).join(',');
  const lines = result.rows.map((row) =>
    result.columns.map((c) => {
      const raw = row[c.key];
      const cell = formatCell(raw, c.format);
      return `"${cell.replace(/"/g, '""')}"`;
    }).join(','),
  );
  return [header, ...lines].join('\n');
}

function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Generic result renderer — works for ANY report's `{ columns, rows, totals }`. */
const ReportResultView: React.FC<{ def: ReportDef; result: ReportResult }> = ({ def, result }) => (
  <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
    <div className="flex items-center justify-between px-4 py-3 border-b border-stone-200 bg-stone-50">
      <span className="text-xs font-bold text-stone-700">{def.title} — {result.rows.length} row(s)</span>
      <button
        onClick={() => downloadCsv(`${def.id}.csv`, toCsv(result))}
        disabled={result.rows.length === 0}
        className="flex items-center gap-1.5 text-[11px] font-semibold text-stone-600 hover:text-red-500 disabled:opacity-40"
      >
        <Download size={13} /> Export CSV
      </button>
    </div>
    {result.rows.length === 0 ? (
      <p className="p-6 text-center text-sm text-stone-400">No data yet.</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 border-b border-stone-200">
            <tr className="text-left text-[10px] uppercase tracking-wide text-stone-500">
              {result.columns.map((c) => <th key={c.key} className="px-4 py-2.5 font-semibold">{c.label}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {result.rows.map((row, i) => (
              <tr key={i}>
                {result.columns.map((c) => (
                  <td key={c.key} className="px-4 py-2.5 text-stone-700">{formatCell(row[c.key], c.format)}</td>
                ))}
              </tr>
            ))}
          </tbody>
          {result.totals && (
            <tfoot className="border-t border-stone-200 bg-stone-50">
              <tr className="text-xs font-bold text-stone-800">
                {result.columns.map((c, i) => (
                  <td key={c.key} className="px-4 py-2.5">
                    {i === 0 ? 'Total' : (c.key in result.totals! ? formatCell(result.totals![c.key], c.format) : '')}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    )}
  </div>
);

const ReportsPage: React.FC = () => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [params, setParams] = useState<Record<string, string>>({});
  const [result, setResult] = useState<ReportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [stockValueCents, setStockValueCents] = useState<number | null>(null);
  const [financingOutstandingCents, setFinancingOutstandingCents] = useState<number | null>(null);

  const selected = useMemo(() => REPORTS.find((r) => r.id === selectedId) || null, [selectedId]);

  // The two stat-row figures that already have real data behind them tonight
  // (stock is computed client-side, financing is a live billing-service
  // endpoint) — the other two mockup stats (damage rate, avg payback) need
  // aggregations no backend computes yet, so they show "—" rather than a
  // fabricated number.
  useEffect(() => {
    REPORTS.find((r) => r.id === 'stock-valuation')?.endpoint({})
      .then((r) => setStockValueCents(r.totals?.valueCents ?? null))
      .catch(() => setStockValueCents(null));
    REPORTS.find((r) => r.id === 'financing-book')?.endpoint({})
      .then((r) => setFinancingOutstandingCents(r.totals?.remainingCents ?? null))
      .catch(() => setFinancingOutstandingCents(null));
  }, []);

  const runReport = async (def: ReportDef, p: Record<string, string>) => {
    setSelectedId(def.id);
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const r = await def.endpoint(p);
      setResult(r);
    } catch (e: any) {
      const msg = String(e?.message || '');
      setError(msg.includes('404')
        ? `${def.title}'s backend is landing tonight — this report will fill in once it deploys.`
        : (msg || `Failed to load ${def.title}`));
    } finally {
      setLoading(false);
    }
  };

  const openReport = (def: ReportDef) => {
    const initial: Record<string, string> = {};
    for (const p of def.params || []) initial[p.key] = p.options?.[0]?.value || '';
    setParams(initial);
    runReport(def, initial);
  };

  return (
    <InventoryPage icon={ClipboardList} title="Reports" subtitle="Every number traces to a unit">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="rounded-xl border border-stone-200 bg-white p-4">
          <div className="text-2xl font-light text-stone-900">{stockValueCents === null ? '—' : money(stockValueCents)}</div>
          <div className="text-[10px] uppercase tracking-wider text-stone-500 mt-1 font-mono">Stock value at cost</div>
        </div>
        <div className="rounded-xl border border-stone-200 bg-white p-4">
          <div className="text-2xl font-light text-stone-900">{financingOutstandingCents === null ? '—' : money(financingOutstandingCents)}</div>
          <div className="text-[10px] uppercase tracking-wider text-stone-500 mt-1 font-mono">Financing outstanding</div>
        </div>
        <div className="rounded-xl border border-stone-200 bg-white p-4">
          <div className="text-2xl font-light text-stone-400">—</div>
          <div className="text-[10px] uppercase tracking-wider text-stone-500 mt-1 font-mono">Damage rate (90d)</div>
        </div>
        <div className="rounded-xl border border-stone-200 bg-white p-4">
          <div className="text-2xl font-light text-stone-400">—</div>
          <div className="text-[10px] uppercase tracking-wider text-stone-500 mt-1 font-mono">Avg payback / unit</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {REPORTS.map((def) => (
          <button
            key={def.id}
            onClick={() => openReport(def)}
            className={`text-left rounded-xl border p-4 flex items-center gap-3 transition ${
              selectedId === def.id ? 'border-red-300 bg-red-50/40' : 'border-stone-200 bg-white hover:border-stone-300'
            }`}
          >
            <div className="w-10 h-10 rounded-xl bg-stone-900 flex items-center justify-center shrink-0">
              <def.icon size={18} className="text-stone-300" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-bold text-stone-900 text-sm">{def.title}</div>
              <div className="text-[11px] text-stone-500 mt-0.5">{def.description}</div>
            </div>
            <ChevronRight size={16} className="text-stone-400 shrink-0" />
          </button>
        ))}
      </div>

      {selected && (
        <div>
          {(selected.params?.length ?? 0) > 0 && (
            <div className="flex flex-wrap gap-3 mb-3">
              {selected.params!.map((p) => (
                <div key={p.key} className="w-48">
                  <label className="block text-[10px] uppercase tracking-wider text-stone-500 mb-1 font-mono">{p.label}</label>
                  {p.type === 'select' ? (
                    <SearchableDropdown
                      options={p.options || []}
                      value={params[p.key] || null}
                      onChange={(v) => {
                        const next = { ...params, [p.key]: v || '' };
                        setParams(next);
                        runReport(selected, next);
                      }}
                      variant="light"
                    />
                  ) : (
                    <input
                      type={p.type === 'date' ? 'date' : 'text'}
                      value={params[p.key] || ''}
                      onChange={(e) => setParams({ ...params, [p.key]: e.target.value })}
                      onBlur={() => runReport(selected, params)}
                      className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm"
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          {loading ? (
            <RubyLoader variant="page" />
          ) : error ? (
            <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600" />
              <div className="text-sm text-amber-900">{error}</div>
            </div>
          ) : result ? (
            <ReportResultView def={selected} result={result} />
          ) : null}
        </div>
      )}

      <p className="mt-4 text-[11px] text-stone-500">
        Every number traces to a unit — click a report to see the rows behind it. No report invents data; each is an
        aggregation over the same unit ledger the rest of Inventory writes.
      </p>
    </InventoryPage>
  );
};

export default ReportsPage;
